import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { logger } from "../../../utils/logger";
import IconReportBug from "../../icons/IconReportBug";
import AnnotateScreenshotModal from "./AnnotateScreenshotModal";
import SubmitTicketModal from "./SubmitTicketModal";

interface ReportBugProps {
  /**
   * Override the default screen-capture flow. When provided, no
   * `getDisplayMedia` prompt is shown and the modal opens immediately.
   */
  onClick?: () => void;
  /**
   * Render the floating pill variant (white bg, red outline, pill shape,
   * `position: fixed` at the bottom-left of the viewport). Mirrors Vue's
   * `<ReportBug isFloatButton />` mode used at the foot of the aside menu.
   * Default is the inline header variant.
   */
  floating?: boolean;
}

// ── Inline header variant ─────────────────────────────────────────────────
// Outlined red chip. Label hides at <= 1024 px and the horizontal padding
// shrinks so the icon stays centered.
const INLINE_BTN_CLASS =
  'inline-flex items-center gap-[6px] h-8 px-3 bg-transparent' +
  ' border border-solid border-[var(--color-danger)] text-[var(--color-danger)]' +
  ' text-[13px] font-hk-medium cursor-pointer whitespace-nowrap' +
  ' transition-[background-color,color] duration-[150ms] ease-linear' +
  ' max-[1024px]:px-[10px]';

// The label inside the inline button — hidden on tablets/phones.
const INLINE_LABEL_CLASS = 'max-[1024px]:hidden';

// ── Floating pill variant ─────────────────────────────────────────────────
// Pinned bottom-left, 40 px circle at rest. On hover the button expands
// horizontally by animating the LABEL'S `max-width` from 0 → 160 px (the
// same trick Vue uses in `.report-bug-float-expand`). The label and the
// padding-right are the two things that animate; the icon margin slides
// open at the same time. Mobile collapses to a 36-px circle.
const FLOAT_BTN_CLASS =
  'fixed left-[22px] bottom-7 z-[1400] inline-flex items-center justify-center' +
  ' h-10 min-w-10 px-[10px] border-[1.5px] border-solid border-[var(--color-danger)]' +
  ' rounded-full bg-white text-[var(--color-danger)]' +
  ' text-sm font-hk-semibold font-[inherit] cursor-pointer whitespace-nowrap overflow-hidden' +
  ' [box-shadow:0_2px_10px_rgba(0,0,0,0.18)]' +
  ' transition-[padding-right,box-shadow] [transition-duration:280ms,200ms]' +
  ' [transition-timing-function:cubic-bezier(0.4,0,0.2,1),ease]' +
  ' enabled:hover:pr-[18px] enabled:hover:[box-shadow:0_4px_14px_rgba(235,52,40,0.28)]' +
  ' enabled:focus-visible:pr-[18px] enabled:focus-visible:[box-shadow:0_4px_14px_rgba(235,52,40,0.28)]' +
  ' focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-danger)] focus-visible:outline-offset-[3px]' +
  ' disabled:opacity-70 disabled:cursor-not-allowed' +
  ' max-[768px]:left-3 max-[768px]:bottom-4 max-[768px]:h-9 max-[768px]:min-w-9' +
  ' max-[768px]:px-[9px] max-[768px]:text-[13px]' +
  ' max-[768px]:enabled:hover:pr-4 max-[768px]:enabled:focus-visible:pr-4';

// The icon — gets a margin-right transition that runs in sync with the
// label's max-width transition so the icon slides left as the label fades in.
const FLOAT_ICON_CLASS =
  'flex-none transition-[margin-right] duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]' +
  ' group-hover:enabled:mr-2 group-focus-visible:enabled:mr-2';

// The label — collapsed by default (`max-w-0` + `opacity-0`), expanded on
// parent hover/focus to a 160 px pill. Pointer-events stay off until the
// label is fully visible.
const FLOAT_LABEL_CLASS =
  'inline-block max-w-0 opacity-0 overflow-hidden whitespace-nowrap pointer-events-none' +
  ' tracking-[0.2px] leading-none' +
  ' transition-[max-width,opacity] [transition-duration:280ms,200ms]' +
  ' [transition-timing-function:cubic-bezier(0.4,0,0.2,1),ease] [transition-delay:0ms,50ms]' +
  ' group-hover:enabled:max-w-[160px] group-hover:enabled:opacity-100 group-hover:enabled:pointer-events-auto' +
  ' group-focus-visible:enabled:max-w-[160px] group-focus-visible:enabled:opacity-100 group-focus-visible:enabled:pointer-events-auto';

type CaptureResult =
  | { status: "success"; dataUrl: string }
  | { status: "cancelled" }
  | { status: "unsupported" }
  | { status: "failed" };

const captureScreenshot = async (): Promise<CaptureResult> => {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getDisplayMedia
  ) {
    return { status: "unsupported" };
  }
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    const video = document.createElement("video");
    video.srcObject = stream;
    await video.play();
    // One animation frame is enough for the first frame to be drawable.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { status: "failed" };
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return { status: "success", dataUrl: canvas.toDataURL("image/png") };
  } catch (err) {
    // User cancellation surfaces as NotAllowedError — bail without opening
    // the ticket modal. Other errors still let the user file a ticket
    // without a screenshot.
    if (err instanceof DOMException && err.name === "NotAllowedError") {
      logger.info("captureScreenshot: cancelled by user");
      return { status: "cancelled" };
    }
    logger.warn("captureScreenshot: failed", err);
    return { status: "failed" };
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
};

type Phase = "idle" | "capturing" | "annotate" | "form";

const ReportBug: React.FC<ReportBugProps> = ({ onClick, floating = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>("idle");
  const [screenshot, setScreenshot] = useState<string | null>(null);

  const handleClick = useCallback(async (): Promise<void> => {
    if (onClick !== undefined) {
      onClick();
      return;
    }
    setPhase("capturing");
    const result = await captureScreenshot();

    if (result.status === "cancelled") {
      // User dismissed the screen-share prompt -- do NOT open any modal.
      setPhase("idle");
      setScreenshot(null);
      return;
    }

    if (result.status === "success") {
      // Screenshot captured -- go to annotation step.
      setScreenshot(result.dataUrl);
      setPhase("annotate");
    } else {
      // Failed or unsupported -- skip annotation, go straight to form.
      setScreenshot(null);
      setPhase("form");
    }
  }, [onClick]);

  const handleAnnotateCancel = useCallback((): void => {
    setPhase("idle");
    setScreenshot(null);
  }, []);

  const handleAnnotateNext = useCallback((annotatedDataUrl: string): void => {
    setScreenshot(annotatedDataUrl);
    setPhase("form");
  }, []);

  const handleFormClose = useCallback((): void => {
    setPhase("idle");
    setScreenshot(null);
  }, []);

  // Mirrors Vue's `hideReportBug = route.path.includes('/login')` guard.
  if (location.pathname.includes("/login")) return null;

  const label = t("header.submitTicket");
  // The floating variant uses Tailwind's `group-hover` so we tag the button
  // as a group; the inline variant doesn't need this.
  const buttonClassName = floating
    ? `group ${FLOAT_BTN_CLASS}`
    : INLINE_BTN_CLASS;
  const iconClassName = floating ? FLOAT_ICON_CLASS : undefined;
  const labelClassName = floating ? FLOAT_LABEL_CLASS : INLINE_LABEL_CLASS;

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => void handleClick()}
        aria-label={label}
        disabled={phase === "capturing"}>
        <IconReportBug size={floating ? 18 : 16} className={iconClassName} />
        <span className={labelClassName}>
          {phase === "capturing" ? t("reportBug.capturing") : label}
        </span>
      </button>
      {phase === "annotate" && screenshot !== null ? (
        <AnnotateScreenshotModal
          open
          screenshotDataUrl={screenshot}
          onNext={handleAnnotateNext}
          onCancel={handleAnnotateCancel}
        />
      ) : null}
      <SubmitTicketModal
        open={phase === "form"}
        onClose={handleFormClose}
        screenshotDataUrl={screenshot}
      />
    </>
  );
};

export default ReportBug;
